import {
  useEffect, forwardRef, useReducer, useImperativeHandle,
} from 'react';
import { nanoid } from 'nanoid';
import { Range, getTrackBackground } from 'react-range';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import Icon from '@/core/icon.js';
import { useTranslation } from '@/helpers/translate';

const SliderType = forwardRef(({
  item, onScreen, onFilter, onValid, renderButtonStartTour,
}, ref) => {
  const { t } = useTranslation();
  const totalRiders = [useSearchZustandStore.getState()?.initParams?.[item.slug]].filter((tmpi) => tmpi);
  const { min, max } = item?.values[0] || [];

  if (parseInt(totalRiders?.[0], 10) > max) {
    totalRiders?.shift();
    totalRiders?.push(max.toString());
  }

  const [currentValue, setCurrentValue] = useReducer(
    (prev, { values, runSearch }) => {
      let newValue = {
        ...prev,
        values: [...prev.values],
        isChange: nanoid(),
        keyList: nanoid(),
      };
      if (values) {
        newValue = { ...newValue, values };
      }
      if (runSearch) {
        newValue.validValues = [...newValue.values];
        newValue.runSearch = nanoid();
      }
      return { ...newValue };
    },
    {
      values: totalRiders,
      validValues: totalRiders,
      isChange: null,
      keyList: nanoid(),
    },
  );

  const isMaxValue = currentValue?.values[0]?.[0] === max;

  const [isOpenModal, setIsOpenModal] = useReducer((prev, newValue) => {
    let tmpValue = newValue;
    if (tmpValue === 'finalclose') {
      tmpValue = false;
    } else if (tmpValue === 'iconclose' || tmpValue === 'outside') {
      // eslint-disable-line no-empty
    } else if (!tmpValue) {
      if (onValid && !currentValue.runSearch) tmpValue = 'willCloseAndValid';
    }

    return tmpValue;
  }, false);

  function clearValue(runSearch, e) {
    if (e?.stopPropagation) e.stopPropagation();
    setCurrentValue({ values: [], runSearch });
    setIsOpenModal(false);
  }

  function getValue(out) {
    if (currentValue?.values?.length) {
      if (out === 'url') return currentValue.values.join(',');
      return currentValue.values.map((v) => (!Number.isNaN(parseFloat(v))
        ? parseFloat(v) : v));
    }
    return null;
  }

  function getOutput() {
    return {
      slug: item.slug,
      url: getValue('url'),
      values: getValue(),
      graphql: {
        [item.slug]: getValue(),
      },
    };
  }

  function isSameDefault() {
    return !currentValue.values.length;
  }

  useImperativeHandle(ref, () => ({
    getValue,
    getOutput,
    clearValue,
    setIsOpenModal,
    isSameDefault,
  }), [currentValue]);

  useEffect(() => {
    if (currentValue.runSearch) {
      if (onValid) onValid();
    }
  }, [currentValue.runSearch]);

  useEffect(() => {
    if (isOpenModal === 'willCloseAndValid') {
      onValid();
      setIsOpenModal('finalclose');
    } else if (isOpenModal === 'iconclose' || isOpenModal === 'outside') {
      onValid(isOpenModal);
      setIsOpenModal('finalclose');
    }
  }, [isOpenModal]);

  function closeOnlyNotSaveValues(action) {
    setCurrentValue({ values: currentValue?.validValues?.length ? [currentValue?.validValues] : [], runSearch: null });
    setIsOpenModal(action);
  }

  function callrunSearch() {
    setCurrentValue({ runSearch: true });
    setIsOpenModal(false);
  }

  function renderTitle() {
    if (currentValue?.values?.length) {
      return (
        <div className="text-sm flex justify-center items-center transition text-white py-[8px] px-[8px] bg-purple rounded-lg cursor-pointer hover:bg-purple/80">
          <Icon name="person" className="size-4.5 mr-2 text-white group-hover:text-white" />
          <span
            onClick={() => {
              setIsOpenModal(true);
            }}
          >
            {currentValue?.values?.map((value) => (
              <p className="" key={nanoid()}>
                <span className="ml-1">{value}</span>
              </p>
            ))}
          </span>
          <span className="w-6" onClick={(e) => clearValue(true, e)}><Icon name="close" className="w-full h-3 mx-1 fill-current text-white" /></span>
        </div>
      );
    }
    return (
      <p className="text-sm text-white flex justify-center whitespace-nowrap w-full items-center transition-all duration-200 py-[8px] px-[8px] rounded-lg cursor-pointer bg-purple-6 hover:bg-purple/70 group" onClick={() => setIsOpenModal(true)}>
        <Icon className="size-4 mr-2 transition-colors duration-200 text-purple-4/70 group-hover:text-white" name={item.slug} />
        {t(item.title)}
        <Icon name="next" className="w-2 h-2 ml-2 transition-colors duration-200 transform rotate-90 text-purple-1 group-hover:text-white" />
      </p>
    );
  }

  function renderContent() {
    return (
      <div>
        <div className="flex items-center w-full">
          <div className="flex-grow">
          <Range
            values={[currentValue?.values[0] || min]}
            step={1}
            min={min}
            max={max}
            onChange={(value) => setCurrentValue({ values: [value], runSearch: null })}
            renderTrack={({ props, children }) => (
              <div
                className="px-4 lg:mx-0"
                onMouseDown={props.onMouseDown}
                onTouchStart={props.onTouchStart}
                style={{
                  ...props.style,
                  height: '36px',
                  display: 'flex',
                  width: '100%',
                }}
              >
                <div
                  ref={props.ref}
                  style={{
                    height: '5px',
                    width: '100%',
                    borderRadius: '4px',
                    background: getTrackBackground({
                      values: [currentValue?.values[0] || 1],
                      colors: ['#2974FF', '#d9d9e0'],
                      min,
                      max,
                    }),
                    alignSelf: 'center',
                  }}
                >
                  {children}
                </div>
              </div>
            )}
            renderThumb={({ props }) => (
              <div className="flex items-center justify-center w-8 h-8 bg-white border rounded-full shadow-button border-white-dark3" {...props}><Icon name="lines" className="h-3 mx-1 fill-current text-purple" /></div>
            )}
          />
          </div>
          <input
            type="text"
            className="block w-16 py-2 ml-2 text-center text-black rounded-md lg:p-2 placeholder-dark hover:border-blueborder-purple-1 focus:ring-4 focus:ring-bluefocus:ring-opacity-25"
            value={isMaxValue ? `${currentValue?.values[0]}+` : currentValue?.values[0] || min}
            min={min}
            max={max}
            onChange={(event) => {
            // eslint-disable-next-line radix
              const value = parseInt(event.target.value);
              if (value >= min && value <= max) { // Vérification des limites
                setCurrentValue({ values: [value], runSearch: null });
              }
            }}
          />
        </div>
      </div>
    );
  }

  function renderModal() {
    return (
      <>
        <div
          className="absolute top-0 left-0 z-30 w-screen h-screen bg-purple-6/50"
          onClick={() => {
            closeOnlyNotSaveValues('outside');
          }}
        />
        <div className="-mt-[38px]">
          <div className="min-w-[350px] rounded-lg bg-white z-40 absolute px-6 pt-6 pb-4 shadow-button">
            <h1 className="pb-4 text-xl font-medium">{t(item.title)}</h1>
            <button
              className="absolute w-4 right-6 top-6"
              onClick={() => {
                closeOnlyNotSaveValues('iconclose');
              }}
            >
              <Icon
                name="close"
                className="w-4 h-4 fill-current text-white-dark4 hover:text-black"
              />
            </button>
            {renderContent()}
            <div className="flex items-center justify-between mt-4 mb-2">
              <button
                className="border-b rounded-none text-purple-5/50 border-dark hover:text-purplehover:border-purple"
                onClick={(e) => clearValue(true, e)}
              >
                {t('search:clean')}
              </button>
              <div>
                {renderButtonStartTour?.()}
                <button
                  className="px-6 py-2 purple-gradient"
                  onClick={() => callrunSearch()}
                >
                  {t('search:save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderFilter() {
    if (!item?.title) return null;
    return (
      <div>
        <p className="pb-2 font-medium text-black">{t(item.title)}</p>
        {renderContent()}
      </div>
    );
  }

  return (
    <>
      {!(onScreen && isOpenModal !== true) || renderTitle()}
      {!(onScreen && isOpenModal === true) || (
        <>
          {renderTitle()}
          {renderModal()}
        </>
      )}
      {!onFilter || renderFilter()}
    </>
  );
});

export default SliderType;
